import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Modal } from 'react-bootstrap';
import { inputStringLength } from 'assets/data/config';
import axios from 'axios';
import apiConfig from '../../constants/apiConfig';
import { toast } from 'react-toastify';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import {
  endLoading,
  startLoading,
} from '../../reducers/loading/loading.action';

type UpdateDialogData = {
  id: string;
  file_name: string;
  workspaceId?: string;
};

type RenameFileDialogProps = {
  onClose: (updated?: unknown) => void;
  show: boolean;
  dialogType?: string;
  updateDialogData: UpdateDialogData;
};

type FormValues = {
  file_name: string;
};

export default function RenameFileDialog({
  onClose,
  show,
  dialogType,
  updateDialogData,
}: RenameFileDialogProps) {
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onChange' });

  const renameFile = async (data: FormValues) => {
    dispatch(startLoading());
    try {
      const response = await axios.patch(
        `${apiConfig.apiGateway.filesBase}/${updateDialogData.id}`,
        {
          name:
            data.file_name + '.' + updateDialogData?.file_name.split('.')[1],
        }
      );
      onClose(response.data);
      toast.success('File Renamed successfully');
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.response?.status?.msg ||
          'Failed to rename file'
      );
    } finally {
      dispatch(endLoading());
    }
  };

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        handleSubmit(renameFile)();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{dialogType}Rename File</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(renameFile)}>
        <Modal.Body>
          <label className="form-label">Display Name</label>
          <Controller
            control={control}
            defaultValue={updateDialogData?.file_name.substring(
              0,
              updateDialogData?.file_name.indexOf('.')
            )}
            name="file_name"
            rules={{ required: true, maxLength: inputStringLength }}
            render={({ field: { onChange, value } }) => (
              <StylishNewInput
                type="text"
                onChange={onChange}
                value={value}
                placeholder="Filename"
              />
            )}
          />
          {errors?.file_name?.type === 'required' && (
            <span className="form-text form-error">
              This is a required field
            </span>
          )}
          {errors?.file_name?.type === 'maxLength' && (
            <span className="form-text form-error">
              Max {inputStringLength} characters allowed
            </span>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              className="button--secondary button--reverse"
              onClick={onClose}
              type="button"
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton className="button--primary" type="submit">
              Rename
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
